import React from 'react';
import env from '../../env';

/**
 * WebPageSchema Component
 * 
 * Implements Schema.org WebPage structured data for generic content pages.
 * Use this for pages that don't fit more specific types (AboutPage, ContactPage, etc.)
 * Follows best practices as defined by Schema.org:
 * - https://schema.org/WebPage
 * 
 * @param {Object} props
 * @param {string} props.name - Page title
 * @param {string} props.description - Page description
 * @param {string} props.url - Page URL (relative or absolute)
 * @param {Object} props.breadcrumb - Breadcrumb data (optional)
 * @param {string} props.image - Page image URL (optional)
 * 
 * @example
 * <WebPageSchema
 *   name="Privacy Policy | Scunci"
 *   description="Scunci's privacy policy"
 *   url="/privacy"
 *   breadcrumb={breadcrumbData}
 * />
 */
export function WebPageSchema({ 
  name, 
  description, 
  url, 
  breadcrumb,
  image 
}) {
  React.useEffect(() => {
    // Validate required data
    if (!name || !url) {
      return;
    }

    // Remove any existing web page schema script
    const existingScript = document.querySelector('script[data-web-page-schema]');
    if (existingScript) {
      existingScript.remove();
    }

    // Build absolute URL
    let absoluteUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      const normalizedUrl = url.startsWith('/') ? url : '/' + url;
      absoluteUrl = env.baseOrigin + normalizedUrl;
    }

    // Build absolute image URL if provided
    let absoluteImageUrl = null;
    if (image) {
      if (image.startsWith('http://') || image.startsWith('https://')) {
        absoluteImageUrl = image;
      } else {
        const normalizedImage = image.startsWith('/') ? image : '/' + image;
        absoluteImageUrl = env.baseOrigin + normalizedImage;
      }
    }

    // Build WebPage schema according to Schema.org specification
    const webPageSchema = {
      "@context": "https://schema.org/",
      "@type": "WebPage",
      "@id": absoluteUrl,
      "name": name,
      "url": absoluteUrl
    };

    // Add description if provided
    if (description) {
      webPageSchema.description = description;
    }

    // Add image if provided
    if (absoluteImageUrl) {
      webPageSchema.image = absoluteImageUrl;
    }

    // Add breadcrumb reference if provided
    if (breadcrumb && breadcrumb.items && breadcrumb.items.length > 0) {
      webPageSchema.breadcrumb = {
        "@id": absoluteUrl + "#breadcrumb"
      };
    }

    // Create and inject JSON-LD script
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-web-page-schema', 'true');
    script.textContent = JSON.stringify(webPageSchema, null, 0);
    document.head.appendChild(script);

    // Cleanup function to remove schema when component unmounts
    return () => {
      const scriptToRemove = document.querySelector('script[data-web-page-schema]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [name, description, url, breadcrumb, image]); // Re-run if any prop changes

  // Return null since we're using direct DOM manipulation
  return null;
}
